import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { consentStore } from '../consent/ConsentStore'
import { ConsentSnapshot } from '../types'

export type ConsentStatus = 'granted' | 'denied' | 'pending'

interface ConsentContextValue {
  consentStatus: ConsentStatus
  grantConsent: () => void
  denyConsent: () => void
}

const ConsentContext = createContext<ConsentContextValue>({
  consentStatus: 'pending',
  grantConsent: () => {},
  denyConsent: () => {},
})

function deriveStatus(snapshot: ConsentSnapshot, decided: boolean): ConsentStatus {
  if (snapshot.ad_storage === 'granted') return 'granted'
  return decided ? 'denied' : 'pending'
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(() =>
    deriveStatus(consentStore.snapshot(), consentStore.isDecided()),
  )

  useEffect(() => {
    const unsubscribe = consentStore.subscribe((snap: ConsentSnapshot) => {
      setConsentStatus(deriveStatus(snap, consentStore.isDecided()))
    })
    return unsubscribe
  }, [])

  const grantConsent = useCallback(() => {
    consentStore.grant()
  }, [])

  const denyConsent = useCallback(() => {
    consentStore.deny()
  }, [])

  return (
    <ConsentContext.Provider value={{ consentStatus, grantConsent, denyConsent }}>
      {children}
    </ConsentContext.Provider>
  )
}

export function useConsent() {
  return useContext(ConsentContext)
}
